/**
 * OrgChartPropertyEditor
 *
 * Property editor for the "org-chart" control type.
 *
 * 7 sections:
 *   1. Data Source        — static JSON textarea + info box
 *   2. Field Mapping      — 2-col grid of field-name text inputs
 *   3. Layout & Card Style— layout select, card-style select, width range
 *   4. Appearance         — avatar/description toggles, connector color picker
 *   5. Interaction        — collapsible toggle, default-expanded select
 *   6. Node Actions       — dynamic list identical to TreeView's action editor
 *   7. Dynamic Link       — per-node URL field, label field, open-in select
 *
 * SRP: responsible ONLY for rendering the org-chart property panel.
 */

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import FormLookupSelect from '../FormContainers/FormLookupSelect';

// ─── Types ────────────────────────────────────────────────────────────────────

interface NodeAction {
  id: string;
  label: string;
  actionType: 'url' | 'form' | 'event';
  urlTemplate?: string;
  openIn?: 'same-tab' | 'new-tab';
  formId?: string;
  formIdField?: string;
  eventName?: string;
}

interface OrgChartConfig {
  data?: unknown;
  idField?: string;
  nameField?: string;
  titleField?: string;
  descriptionField?: string;
  childrenField?: string;
  avatarField?: string;
  colorField?: string;
  layout?: 'vertical' | 'horizontal';
  cardStyle?: 'minimal' | 'detailed' | 'compact';
  showDescription?: boolean;
  showAvatar?: boolean;
  connectorColor?: string;
  cardMinWidth?: number;
  cardMaxWidth?: number;
  collapsible?: boolean;
  defaultExpanded?: boolean | number;
  nodeActions?: NodeAction[];
  nodeLinkUrlField?: string;
  nodeLinkLabelField?: string;
  nodeLinkOpenIn?: 'same-tab' | 'new-tab';
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface OrgChartPropertyEditorProps {
  control: FormControl;
  onUpdateControl: (patch: Partial<FormControl>) => void;
  isDark: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function makeInputStyle(isDark: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 5,
    border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
    background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
    color: isDark ? '#e2e8f0' : '#1e293b',
    fontSize: 12,
    boxSizing: 'border-box' as const,
  };
}

function makeSelectStyle(isDark: boolean): React.CSSProperties {
  return {
    ...makeInputStyle(isDark),
    cursor: 'pointer',
  };
}

function makeLabelStyle(isDark: boolean): React.CSSProperties {
  return {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 3,
    color: isDark ? '#94a3b8' : '#64748b',
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionTitle: React.FC<{ title: string; isDark: boolean; children?: React.ReactNode }> = ({
  title,
  isDark,
  children,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 10,
    }}
  >
    <h4
      style={{
        margin: 0,
        fontSize: 12,
        fontWeight: 700,
        color: isDark ? '#e2e8f0' : '#1e293b',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {title}
    </h4>
    {children}
  </div>
);

const Section: React.FC<{ title: string; isDark: boolean; children: React.ReactNode; actionSlot?: React.ReactNode }> = ({
  title,
  isDark,
  children,
  actionSlot,
}) => (
  <div
    style={{
      marginBottom: 16,
      padding: '10px 12px',
      borderRadius: 6,
      background: isDark ? 'rgba(15,23,42,0.4)' : 'rgba(241,245,249,0.8)',
      border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0'}`,
    }}
  >
    <SectionTitle title={title} isDark={isDark}>
      {actionSlot}
    </SectionTitle>
    {children}
  </div>
);

const Field: React.FC<{ label: string; isDark: boolean; children: React.ReactNode }> = ({
  label,
  isDark,
  children,
}) => (
  <div style={{ marginBottom: 8 }}>
    <label style={makeLabelStyle(isDark)}>{label}</label>
    {children}
  </div>
);

const Toggle: React.FC<{
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  isDark: boolean;
}> = ({ label, checked, onChange, isDark }) => (
  <label
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
      fontSize: 12,
      color: isDark ? '#e2e8f0' : '#1e293b',
      marginBottom: 6,
      userSelect: 'none',
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{ accentColor: '#309B48', cursor: 'pointer' }}
    />
    {label}
  </label>
);

// ─── Node Action Row ──────────────────────────────────────────────────────────

const NodeActionRow = memo<{
  action: NodeAction;
  index: number;
  isDark: boolean;
  onUpdate: (index: number, action: NodeAction) => void;
  onRemove: (index: number) => void;
}>(function NodeActionRow({ action, index, isDark, onUpdate, onRemove }) {
  const inputStyle = useMemo(() => makeInputStyle(isDark), [isDark]);
  const selectStyle = useMemo(() => makeSelectStyle(isDark), [isDark]);
  const labelStyle = useMemo(() => makeLabelStyle(isDark), [isDark]);

  const set = <K extends keyof NodeAction>(key: K, value: NodeAction[K]) =>
    onUpdate(index, { ...action, [key]: value });

  return (
    <div
      style={{
        marginBottom: 10,
        padding: '8px 10px',
        borderRadius: 5,
        background: isDark ? 'rgba(30,41,59,0.6)' : '#f8fafc',
        border: `1px solid ${isDark ? 'rgba(71,85,105,0.5)' : '#e2e8f0'}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b' }}>
          Action {index + 1}
        </span>
        <button
          type="button"
          onClick={() => onRemove(index)}
          title="Remove action"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: isDark ? '#f87171' : '#dc2626',
            fontSize: 14,
            lineHeight: 1,
            padding: '0 2px',
          }}
          aria-label={`Remove action ${index + 1}`}
        >
          ✕
        </button>
      </div>

      {/* Label */}
      <div style={{ marginBottom: 6 }}>
        <label style={labelStyle}>Label</label>
        <input
          type="text"
          value={action.label}
          onChange={(e) => set('label', e.target.value)}
          placeholder="Action label"
          style={inputStyle}
          aria-label={`Action ${index + 1} label`}
        />
      </div>

      {/* Action Type */}
      <div style={{ marginBottom: 6 }}>
        <label style={labelStyle}>Action Type</label>
        <select
          value={action.actionType}
          onChange={(e) => set('actionType', e.target.value as NodeAction['actionType'])}
          style={selectStyle}
          aria-label={`Action ${index + 1} type`}
        >
          <option value="url">URL</option>
          <option value="form">Form</option>
          <option value="event">Event</option>
        </select>
      </div>

      {/* URL fields */}
      {action.actionType === 'url' && (
        <>
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>URL Template</label>
            <input
              type="text"
              value={action.urlTemplate ?? ''}
              onChange={(e) => set('urlTemplate', e.target.value)}
              placeholder="https://example.com/{{node.id}}"
              style={inputStyle}
              aria-label={`Action ${index + 1} URL template`}
            />
          </div>
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>Open In</label>
            <select
              value={action.openIn ?? 'new-tab'}
              onChange={(e) => set('openIn', e.target.value as NodeAction['openIn'])}
              style={selectStyle}
              aria-label={`Action ${index + 1} open in`}
            >
              <option value="same-tab">Same Tab</option>
              <option value="new-tab">New Tab</option>
            </select>
          </div>
        </>
      )}

      {/* Form fields */}
      {action.actionType === 'form' && (
        <>
          <FormLookupSelect
            label="Form ID"
            value={action.formId ?? null}
            onChange={(v) => set('formId', v !== null ? String(v) : undefined)}
            placeholder="Select a form..."
          />
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>Form ID Field (dynamic)</label>
            <input
              type="text"
              value={action.formIdField ?? ''}
              onChange={(e) => set('formIdField', e.target.value)}
              placeholder="node.formId"
              style={inputStyle}
              aria-label={`Action ${index + 1} form ID field`}
            />
          </div>
        </>
      )}

      {/* Event fields */}
      {action.actionType === 'event' && (
        <div style={{ marginBottom: 6 }}>
          <label style={labelStyle}>Event Name</label>
          <input
            type="text"
            value={action.eventName ?? ''}
            onChange={(e) => set('eventName', e.target.value)}
            placeholder="org-chart:node-clicked"
            style={inputStyle}
            aria-label={`Action ${index + 1} event name`}
          />
        </div>
      )}
    </div>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────────

export const OrgChartPropertyEditor: React.FC<OrgChartPropertyEditorProps> = ({
  control,
  onUpdateControl,
  isDark,
}) => {
  const config = useMemo(
    () => ((control.config ?? {}) as OrgChartConfig),
    [control.config],
  );

  // JSON textarea state for data source
  const [dataJson, setDataJson] = useState<string>(() => {
    if (config.data === undefined || config.data === null) return '';
    try {
      return JSON.stringify(config.data, null, 2);
    } catch {
      return '';
    }
  });
  const [dataJsonError, setDataJsonError] = useState<string | null>(null);

  // Track whether the textarea is focused so external data changes don't clobber active editing.
  const dataJsonFocusedRef = useRef(false);

  // Sync textarea when config.data is updated externally (e.g. undo/redo).
  useEffect(() => {
    if (dataJsonFocusedRef.current) return;
    if (config.data === undefined || config.data === null) {
      setDataJson('');
    } else {
      try {
        setDataJson(JSON.stringify(config.data, null, 2));
      } catch {
        // keep whatever the user has typed
      }
    }
  }, [config.data]);

  const setConfig = useCallback(
    <K extends keyof OrgChartConfig>(key: K, value: OrgChartConfig[K]) => {
      onUpdateControl({
        config: {
          ...control.config,
          [key]: value,
        },
      });
    },
    [control.config, onUpdateControl],
  );

  const inputStyle = useMemo(() => makeInputStyle(isDark), [isDark]);
  const selectStyle = useMemo(() => makeSelectStyle(isDark), [isDark]);
  const labelStyle = useMemo(() => makeLabelStyle(isDark), [isDark]);

  // ── Section 1: Data Source ─────────────────────────────────────────────────

  // Validate while typing but only commit to config on blur (avoids expensive
  // re-renders of the org chart on every keystroke).
  const handleDataJsonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const raw = e.target.value;
    setDataJson(raw);
    if (raw.trim() === '') {
      setDataJsonError(null);
    } else {
      try {
        JSON.parse(raw);
        setDataJsonError(null);
      } catch {
        setDataJsonError('Invalid JSON — fix before saving.');
      }
    }
  }, []);

  const handleDataJsonBlur = useCallback(() => {
    dataJsonFocusedRef.current = false;
    const raw = dataJson.trim();
    if (!raw) {
      setDataJsonError(null);
      setConfig('data', undefined);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setDataJsonError(null);
      setConfig('data', parsed);
    } catch {
      setDataJsonError('Invalid JSON — fix before saving.');
    }
  }, [dataJson, setConfig]);

  // ── Section 6: Node Actions ────────────────────────────────────────────────

  const nodeActions: NodeAction[] = config.nodeActions ?? [];

  const handleAddAction = useCallback(() => {
    const newAction: NodeAction = {
      id: `action-${Date.now()}`,
      label: 'Action',
      actionType: 'url',
      urlTemplate: '',
      openIn: 'new-tab',
    };
    setConfig('nodeActions', [...nodeActions, newAction]);
  }, [nodeActions, setConfig]);

  const handleUpdateAction = useCallback(
    (index: number, updated: NodeAction) => {
      const next = [...nodeActions];
      next[index] = updated;
      setConfig('nodeActions', next);
    },
    [nodeActions, setConfig],
  );

  const handleRemoveAction = useCallback(
    (index: number) => {
      setConfig('nodeActions', nodeActions.filter((_, i) => i !== index));
    },
    [nodeActions, setConfig],
  );

  // ── defaultExpanded helpers ────────────────────────────────────────────────

  const defaultExpandedStr = useMemo(() => {
    const v = config.defaultExpanded;
    if (v === undefined || v === null) return 'none';
    if (v === true) return 'all';
    if (typeof v === 'number') return String(v);
    return 'none';
  }, [config.defaultExpanded]);

  const handleDefaultExpandedChange = useCallback((val: string) => {
    if (val === 'none') setConfig('defaultExpanded', undefined);
    else if (val === 'all') setConfig('defaultExpanded', true);
    else setConfig('defaultExpanded', Number(val));
  }, [setConfig]);

  // ── Connector Color ────────────────────────────────────────────────────────

  const connectorColor = config.connectorColor ?? '#94a3b8';
  const [connectorColorText, setConnectorColorText] = useState(connectorColor);

  // Keep text in sync if config changes externally
  useEffect(() => {
    setConnectorColorText(config.connectorColor ?? '#94a3b8');
  }, [config.connectorColor]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '4px 0' }}>

      {/* ── 1. Data Source ────────────────────────────────────────────────── */}
      <Section title="Data Source" isDark={isDark}>
        <Field label="Static JSON Data" isDark={isDark}>
          <textarea
            rows={5}
            value={dataJson}
            onChange={handleDataJsonChange}
            onFocus={() => { dataJsonFocusedRef.current = true; }}
            onBlur={handleDataJsonBlur}
            placeholder='[{ "id": "1", "name": "CEO", "title": "Chief Executive", "children": [] }]'
            style={{
              ...inputStyle,
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              resize: 'vertical',
              lineHeight: 1.4,
            }}
            aria-label="Static JSON data for org chart"
            spellCheck={false}
          />
          {dataJsonError && (
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 11,
                color: isDark ? '#f87171' : '#dc2626',
              }}
              role="alert"
            >
              {dataJsonError}
            </p>
          )}
        </Field>

        {/* Expected shape info */}
        <div
          style={{
            marginTop: 6,
            padding: '6px 8px',
            borderRadius: 4,
            background: isDark ? 'rgba(48,155,72,0.08)' : 'rgba(48,155,72,0.06)',
            border: `1px solid ${isDark ? 'rgba(48,155,72,0.25)' : 'rgba(48,155,72,0.2)'}`,
          }}
        >
          <p style={{ margin: '0 0 3px', fontSize: 11, color: isDark ? '#86efac' : '#166534', fontFamily: 'monospace' }}>
            {'{ id, name, title, description, children: [] }'}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>
            Or use data binding to connect live data.
          </p>
        </div>
      </Section>

      {/* ── 2. Field Mapping ──────────────────────────────────────────────── */}
      <Section title="Field Mapping" isDark={isDark}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0 10px',
          }}
        >
          {/* ID Field */}
          <Field label="ID Field" isDark={isDark}>
            <input
              type="text"
              value={config.idField ?? ''}
              onChange={(e) => setConfig('idField', e.target.value || undefined)}
              placeholder="id"
              style={inputStyle}
              aria-label="ID field name"
            />
          </Field>

          {/* Name Field */}
          <Field label="Name Field" isDark={isDark}>
            <input
              type="text"
              value={config.nameField ?? ''}
              onChange={(e) => setConfig('nameField', e.target.value || undefined)}
              placeholder="name"
              style={inputStyle}
              aria-label="Name field name"
            />
          </Field>

          {/* Title Field */}
          <Field label="Title Field" isDark={isDark}>
            <input
              type="text"
              value={config.titleField ?? ''}
              onChange={(e) => setConfig('titleField', e.target.value || undefined)}
              placeholder="title"
              style={inputStyle}
              aria-label="Title field name"
            />
          </Field>

          {/* Description Field */}
          <Field label="Description Field" isDark={isDark}>
            <input
              type="text"
              value={config.descriptionField ?? ''}
              onChange={(e) => setConfig('descriptionField', e.target.value || undefined)}
              placeholder="description"
              style={inputStyle}
              aria-label="Description field name"
            />
          </Field>

          {/* Children Field */}
          <Field label="Children Field" isDark={isDark}>
            <input
              type="text"
              value={config.childrenField ?? ''}
              onChange={(e) => setConfig('childrenField', e.target.value || undefined)}
              placeholder="children"
              style={inputStyle}
              aria-label="Children field name"
            />
          </Field>

          {/* Avatar Field */}
          <Field label="Avatar Field" isDark={isDark}>
            <input
              type="text"
              value={config.avatarField ?? ''}
              onChange={(e) => setConfig('avatarField', e.target.value || undefined)}
              placeholder="avatarUrl — optional"
              style={inputStyle}
              aria-label="Avatar URL field name"
            />
          </Field>

          {/* Color Field — spans both columns */}
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Color Field" isDark={isDark}>
              <input
                type="text"
                value={config.colorField ?? ''}
                onChange={(e) => setConfig('colorField', e.target.value || undefined)}
                placeholder="color — optional hex"
                style={inputStyle}
                aria-label="Color field name"
              />
            </Field>
          </div>
        </div>
      </Section>

      {/* ── 3. Layout & Card Style ────────────────────────────────────────── */}
      <Section title="Layout & Card Style" isDark={isDark}>
        <Field label="Layout" isDark={isDark}>
          <select
            value={config.layout ?? 'vertical'}
            onChange={(e) => setConfig('layout', e.target.value as OrgChartConfig['layout'])}
            style={selectStyle}
            aria-label="Chart layout direction"
          >
            <option value="vertical">Vertical (top-down)</option>
            <option value="horizontal">Horizontal (left-right)</option>
          </select>
        </Field>

        <Field label="Card Style" isDark={isDark}>
          <select
            value={config.cardStyle ?? 'detailed'}
            onChange={(e) => setConfig('cardStyle', e.target.value as OrgChartConfig['cardStyle'])}
            style={selectStyle}
            aria-label="Card style"
          >
            <option value="detailed">Detailed</option>
            <option value="compact">Compact</option>
            <option value="minimal">Minimal</option>
          </select>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
          <Field label="Card Min Width (px)" isDark={isDark}>
            <input
              type="number"
              value={config.cardMinWidth ?? 180}
              min={80}
              max={600}
              step={10}
              onChange={(e) => setConfig('cardMinWidth', Number(e.target.value))}
              style={inputStyle}
              aria-label="Card minimum width in pixels"
            />
          </Field>

          <Field label="Card Max Width (px)" isDark={isDark}>
            <input
              type="number"
              value={config.cardMaxWidth ?? 240}
              min={80}
              max={800}
              step={10}
              onChange={(e) => setConfig('cardMaxWidth', Number(e.target.value))}
              style={inputStyle}
              aria-label="Card maximum width in pixels"
            />
          </Field>
        </div>
      </Section>

      {/* ── 4. Appearance ─────────────────────────────────────────────────── */}
      <Section title="Appearance" isDark={isDark}>
        <Toggle
          label="Show Avatar"
          checked={config.showAvatar ?? true}
          onChange={(v) => setConfig('showAvatar', v)}
          isDark={isDark}
        />
        <Toggle
          label="Show Description"
          checked={config.showDescription ?? true}
          onChange={(v) => setConfig('showDescription', v)}
          isDark={isDark}
        />

        <div style={{ marginTop: 6 }}>
          <label style={labelStyle}>Connector Color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="color"
              value={connectorColor.startsWith('#') ? connectorColor : '#94a3b8'}
              onChange={(e) => {
                const v = e.target.value;
                setConnectorColorText(v);
                setConfig('connectorColor', v);
              }}
              style={{
                width: 34,
                height: 28,
                border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                borderRadius: 4,
                padding: 2,
                cursor: 'pointer',
                background: 'none',
                flexShrink: 0,
              }}
              aria-label="Connector color swatch"
              title="Connector color"
            />
            <input
              type="text"
              value={connectorColorText}
              onChange={(e) => setConnectorColorText(e.target.value)}
              onBlur={() => {
                const trimmed = connectorColorText.trim();
                if (trimmed) {
                  setConfig('connectorColor', trimmed);
                } else {
                  setConnectorColorText('#94a3b8');
                  setConfig('connectorColor', '#94a3b8');
                }
              }}
              placeholder="#94a3b8"
              style={{
                ...inputStyle,
                width: 90,
                fontFamily: 'monospace',
              }}
              aria-label="Connector color hex value"
              spellCheck={false}
            />
          </div>
        </div>
      </Section>

      {/* ── 5. Interaction ────────────────────────────────────────────────── */}
      <Section title="Interaction" isDark={isDark}>
        <Toggle
          label="Collapsible"
          checked={config.collapsible ?? true}
          onChange={(v) => setConfig('collapsible', v)}
          isDark={isDark}
        />

        <Field label="Default Expanded" isDark={isDark}>
          <select
            value={defaultExpandedStr}
            onChange={(e) => handleDefaultExpandedChange(e.target.value)}
            style={selectStyle}
            aria-label="Default expanded level"
          >
            <option value="none">None (all collapsed)</option>
            <option value="all">All (fully expanded)</option>
            <option value="1">1 level</option>
            <option value="2">2 levels</option>
            <option value="3">3 levels</option>
          </select>
        </Field>
      </Section>

      {/* ── 6. Node Actions ───────────────────────────────────────────────── */}
      <Section
        title="Node Actions"
        isDark={isDark}
        actionSlot={
          <button
            type="button"
            onClick={handleAddAction}
            style={{
              padding: '3px 10px',
              borderRadius: 4,
              border: `1px solid ${isDark ? 'rgba(48,155,72,0.5)' : '#309B48'}`,
              background: isDark ? 'rgba(48,155,72,0.12)' : 'rgba(48,155,72,0.08)',
              color: isDark ? '#86efac' : '#166534',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            aria-label="Add node action"
          >
            ＋ Add Action
          </button>
        }
      >
        {nodeActions.length === 0 && (
          <p style={{ margin: 0, fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>
            No actions yet. Click ＋ Add Action to create one.
          </p>
        )}
        {nodeActions.map((action, i) => (
          <NodeActionRow
            key={action.id || `action-${i}`}
            action={action}
            index={i}
            isDark={isDark}
            onUpdate={handleUpdateAction}
            onRemove={handleRemoveAction}
          />
        ))}
      </Section>

      {/* ── 7. Dynamic Link (per node) ────────────────────────────────────── */}
      <Section title="Dynamic Link (per node)" isDark={isDark}>
        <Field label="URL Field" isDark={isDark}>
          <input
            type="text"
            value={config.nodeLinkUrlField ?? ''}
            onChange={(e) => setConfig('nodeLinkUrlField', e.target.value || undefined)}
            placeholder="node.linkUrl"
            style={inputStyle}
            aria-label="Per-node URL field"
          />
        </Field>

        <Field label="Button Label Field" isDark={isDark}>
          <input
            type="text"
            value={config.nodeLinkLabelField ?? ''}
            onChange={(e) => setConfig('nodeLinkLabelField', e.target.value || undefined)}
            placeholder="node.linkLabel"
            style={inputStyle}
            aria-label="Per-node button label field"
          />
        </Field>

        <Field label="Open In" isDark={isDark}>
          <select
            value={config.nodeLinkOpenIn ?? 'new-tab'}
            onChange={(e) => setConfig('nodeLinkOpenIn', e.target.value as OrgChartConfig['nodeLinkOpenIn'])}
            style={selectStyle}
            aria-label="Per-node link open in"
          >
            <option value="same-tab">Same Tab</option>
            <option value="new-tab">New Tab</option>
          </select>
        </Field>
      </Section>
    </div>
  );
};

OrgChartPropertyEditor.displayName = 'OrgChartPropertyEditor';

// ─── PropertyEditor registry entry ───────────────────────────────────────────

export const ORG_CHART_PROPERTY_EDITOR_TYPE = 'org-chart' as const;

/**
 * OrgChartPropertyEditorRegistration
 *
 * PropertyEditor object for use with PropertyEditorRegistry.register().
 * Bridges PropertyEditorProps (onConfigChange / onControlChange) to the
 * standalone OrgChartPropertyEditor FC.
 *
 * isDark is not available from PropertyEditorProps — default to false.
 * Hosts that need dark-mode support should use OrgChartPropertyEditor FC directly.
 */
export const OrgChartPropertyEditorRegistration: PropertyEditor = {
  type: ORG_CHART_PROPERTY_EDITOR_TYPE,
  label: 'Org Chart',
  description: 'Configure the org-chart control: data source, field mapping, layout, actions, and dynamic links',
  version: '1.0.0',
  render: (props: PropertyEditorProps) => {
    const { control, onControlChange } = props;
    const isDark =
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('dark');
    return (
      <OrgChartPropertyEditor
        control={control}
        onUpdateControl={(patch) => onControlChange(patch)}
        isDark={isDark}
      />
    );
  },
};

export { OrgChartPropertyEditor as default };
